/**
 * Classe para construção de filtros no formato de query string
 * Exemplo: ?page=0&size=5&nome=Teste
 */
import { PAGINATION } from '../utils/PaginacaoUtils';

export class FilterBuilder {
  /**
   * Constrói uma query string a partir de um objeto de filtros
   * @param {Object} filterObj - Objeto contendo os parâmetros de filtro
   * @returns {String} Query string formatada
   */
  static buildQueryString(filterObj) {
    if (!filterObj || Object.keys(filterObj).length === 0) {
      return '';
    }

    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(filterObj)) {
      // Ignora chaves com valores undefined, null ou string vazia
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }

  /**
   * Constrói um objeto de filtro com paginação padrão
   * @param {Number} page - Página atual (início em 0)
   * @param {Number} size - Tamanho da página
   * @returns {Object} Objeto de filtro com paginação
   */
  static withPagination(page = PAGINATION.DEFAULT_FIRST_PAGE, size = PAGINATION.DEFAULT_PAGE_SIZE) {
    return {
      page,
      size
    };
  }

  /**
   * Mescla múltiplos objetos de filtro em um único objeto
   * @param {...Object} filters - Objetos de filtro para mesclar
   * @returns {Object} Objeto de filtro mesclado
   */
  static merge(...filters) {
    return Object.assign({}, ...filters.filter(f => f && typeof f === 'object'));
  }

  /**
   * Converte uma string de consulta em um objeto de filtro
   * @param {String} queryString - String de consulta (com ou sem '?')
   * @returns {Object} Objeto com os parâmetros da query string
   */
  static parseQueryString(queryString) {
    if (!queryString) return {};
    
    // Remove o '?' inicial se existir
    const query = queryString.startsWith('?') ? queryString.substring(1) : queryString;
    const params = new URLSearchParams(query);
    const result = {};
    
    for (const [key, value] of params.entries()) {
      result[key] = value;
    }
    
    return result;
  }

  /**
   * Aplica filtro com paginação
   * @param {Number} page - Número da página atual
   * @param {Object} filterObj - Objeto com os filtros a serem aplicados
   * @param {Number} size - Tamanho da página (opcional)
   * @returns {String} Query string formatada com paginação e filtros
   */
  static applyPageFilter(page, size = PAGINATION.DEFAULT_PAGE_SIZE, filterObj) {
    const paginationFilter = FilterBuilder.withPagination(page, size);
    const mergedFilter = FilterBuilder.merge(paginationFilter, filterObj);
    return FilterBuilder.buildQueryString(mergedFilter);
  }
}

/**
 * Exporta funções úteis para filtros
 */
export const FiltroUtils = {
  /**
   * Constrói uma query string a partir de um objeto de filtros
   * @param {Object} filterObj - Objeto de filtro
   * @returns {String} Query string formatada
   */
  buildQuery: (filterObj) => FilterBuilder.buildQueryString(filterObj),
  
  /**
   * Cria um filtro com paginação
   * @param {Number} page - Número da página
   * @param {Number} size - Tamanho da página
   * @returns {Object} Objeto de filtro
   */
  createPaginationFilter: (page, size) => FilterBuilder.withPagination(page, size),
  
  /**
   * Combina múltiplos filtros em um
   * @param {...Object} filters - Filtros para combinar
   * @returns {Object} Filtro combinado
   */
  combineFilters: (...filters) => FilterBuilder.merge(...filters),
  
  /**
   * Converte uma query string em objeto
   * @param {String} queryString - Query string para converter
   * @returns {Object} Objeto de filtro
   */
  parseQuery: (queryString) => FilterBuilder.parseQueryString(queryString),

  /**
   * Aplica filtro com paginação e retorna a query string formatada
   * @param {Number} page - Número da página
   * @param {Number} size - Tamanho da página (opcional)
   * @param {Object} filterObj - Objeto com os filtros a serem aplicados
   * @returns {String} Query string formatada
   */
  applyPageFilter: (page, size, filterObj) => FilterBuilder.applyPageFilter(page, size, filterObj),

  /**
   * Aplica filtro com paginação e retorna o objeto combinado
   * @param {Number} page - Número da página
   * @param {Object} filterObj - Objeto com os filtros a serem aplicados
   * @param {Number} size - Tamanho da página (opcional)
   * @returns {Object} Objeto de filtro combinado
   */
  createPageFilter: (page, filterObj, size = PAGINATION.DEFAULT_PAGE_SIZE) => {
    const paginationFilter = FilterBuilder.withPagination(page, size);
    return FilterBuilder.merge(paginationFilter, filterObj);
  }
};

export default FiltroUtils;